"use client";

import React from "react";
import { motion } from "framer-motion";
import { Shield, Check } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
const trustPoints = [{
  title: "We Buy Our Own Subscriptions",
  body: "Every VPN we review is purchased with our own money — just like you would. We never accept free review copies that might create bias in our testing."
}, {
  title: "We Update Constantly",
  body: "The VPN industry changes fast. We re-test and update every review and ranking regularly to ensure you always have the most current and accurate information available."
}, {
  title: "We Disclose Everything",
  body: "We fully disclose our affiliate relationships, our testing methodology, and our editorial policies. You deserve to know exactly how we operate — and we have nothing to hide."
}, {
  title: "We've Been Doing This For Years",
  body: "Our team has been researching and reviewing VPNs and privacy tools for over a decade. We've seen VPNs come and go — and we know what genuine privacy protection actually looks like."
}, {
  title: "Real Testing. Real Devices.",
  body: "Every speed test, leak test, and streaming test you read about on VPNGENIX was conducted on real devices, on real networks, by real human testers — not automated scripts."
}, {
  title: "We Tell You The Bad Stuff Too",
  body: "No VPN is perfect. We highlight the weaknesses, limitations, and drawbacks of every VPN we review — because an honest review includes the negatives, not just the positives."
}];
const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};
export default function TrustPointsGrid() {
  return <section id="trustpointsgrid" className="bg-background py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        {}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }} className="mb-20 rounded-md border-l-4 border-primary bg-foreground p-8 text-background shadow-lg md:p-10">
          <div className="mb-6 flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-md bg-primary/20">
              <Shield className="h-6 w-6 text-primary" />
            </div>
            <h2 className="font-default text-2xl font-bold md:text-3xl">
              Our Editorial Independence Promise
            </h2>
          </div>
          <div className="space-y-4 text-background/80 md:text-lg">
            <p>
              VPNGENIX operates under a strict editorial independence policy.
              While we do earn affiliate commissions when readers purchase VPNs
              through our links — at no additional cost to you — these
              commissions have zero influence over our rankings, ratings, or
              recommendations.
            </p>
            <p>
              Our testing process is completed before any commercial relationship
              is considered. A VPN that pays higher commissions does NOT receive
              a higher ranking. A VPN that performs better in our testing ALWAYS
              ranks higher — period.
            </p>
            <p>
              If we find a VPN falls short, we say so. Publicly. In writing.
              With the data to back it up.
            </p>
            <p className="pt-4 font-semibold text-background">
              That is our promise to you.
              <br />— The VPNGENIX Editorial Team
            </p>
          </div>
        </motion.div>

        {}
        <div>
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="mb-12 text-center">
            <h2 className="font-default text-3xl font-bold text-foreground md:text-4xl lg:text-5xl">
              Why Thousands of Privacy-Conscious Users Trust VPNGENIX
            </h2>
          </motion.div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.1
        }} className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {trustPoints.map((point, i) => <motion.div key={i} data-index={i} variants={itemVariants}>
                <Card className="flex h-full flex-col rounded-md border-border bg-card shadow-md transition-shadow hover:shadow-lg">
                  <CardHeader>
                    <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Check className="h-6 w-6" />
                    </div>
                    <CardTitle className="font-default text-xl text-card-foreground">
                      {point.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex-grow">
                    <p className="text-muted-foreground leading-relaxed">
                      {point.body}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>)}
          </motion.div>
        </div>
      </div>
    </section>;
}